import { useState, useEffect } from 'react';

const HERO_IMG = "https://public.readdy.ai/ai/img_res/edited_925529fea0694e525e30cea55993700d_002ca9bf.jpg";

const SERVICES = [
  { icon: 'ri-home-gear-line', title: 'Apartment Unit Turns & Make-Readys', desc: 'Fast, consistent unit make-readys that minimize vacancy time. Painting, flooring repairs, drywall patching, fixture replacements, and full make-ready checklists — all to a uniform standard across every unit.' },
  { icon: 'ri-community-line', title: 'Common Area Maintenance & Upkeep', desc: 'Lobbies, hallways, amenity areas, leasing offices, and all shared spaces — routinely maintained to keep your community attractive, safe, and resident-ready at all times.' },
  { icon: 'ri-window-2-line', title: 'Exterior & Facade Repairs', desc: 'Comprehensive exterior upkeep — cladding, siding, facade panels, window seals, and weatherproofing to protect your multi-family property from the elements and maintain curb appeal.' },
  { icon: 'ri-drop-line', title: 'Waterproofing & Structural Repairs', desc: 'Balcony waterproofing, below-grade sealing, roof membrane repairs, foundation cracks, and structural restoration — eliminating water intrusion and keeping your building code-compliant.' },
  { icon: 'ri-stairs-line', title: 'Balcony & Stairwell Restoration', desc: 'Full restoration of balconies, stairwells, and railings — waterproofing, surface repairs, and safety compliance checks phased to minimize disruption to residents.' },
  { icon: 'ri-calendar-check-line', title: 'Preventive Maintenance Programs', desc: 'Custom monthly, quarterly, or semi-annual maintenance contracts for apartment communities — reducing emergency repairs by up to 60%, extending asset life, and protecting resident satisfaction year-round.' },
];

const WHY_ITEMS = [
  { icon: 'ri-shield-check-line', title: 'Licensed & Insured', desc: 'Georgia State licensed contractor with full liability insurance and workers\' compensation. Every apartment project — zero risk for your community or ownership group.' },
  { icon: 'ri-time-line', title: 'On-Time Unit Turns', desc: 'We respect your lease schedule and resident commitments. Every unit turn and make-ready is delivered on time, within budget, and to a consistent standard.' },
  { icon: 'ri-customer-service-2-line', title: '24/7 Emergency Response', desc: 'Urgent water intrusion, structural failures, or facade hazards — our emergency line is always on, and we deploy within 2–4 hours to protect your residents and asset.' },
  { icon: 'ri-map-pin-2-line', title: 'Deep Atlanta Market Knowledge', desc: 'Rooted in Greater Atlanta\'s apartment market — we know local codes, HOA requirements, inspectors, and supplier networks better than anyone.' },
  { icon: 'ri-building-4-line', title: 'One Company, All Services', desc: 'Unit turns, structural repairs, common area upgrades, balcony restoration, and preventive programs — no need to manage multiple contractors for your community.' },
  { icon: 'ri-trophy-line', title: 'Pursuit of Excellence', desc: 'Every apartment community we serve is held to our highest internal standard. We don\'t sign off until every unit and every space reflects that commitment.' },
];

const TESTIMONIALS = [
  {
    name: 'James B.',
    role: 'Apartment Manager · Norcross, GA',
    text: 'We brought FMA Crew in for urgent facade repairs on our 200-unit apartment complex after water intrusion was reported. They responded within hours, diagnosed the issue accurately, and completed the waterproofing and masonry repair faster than we expected. Clean, professional, and no cutting corners.',
    service: 'Multi-Family & Apartment Maintenance',
    initials: 'JB',
  },
  {
    name: 'Karen P.',
    role: 'Property Manager · Marietta, GA',
    text: 'FMA Crew handled unit turns and exterior re-sealing across all four buildings of our apartment community. The team was organized, communicated clearly at every step, and the finished work looks better than new. We immediately signed a preventive maintenance contract with them.',
    service: 'Multi-Family & Apartment Maintenance',
    initials: 'KP',
  },
  {
    name: 'Robert T.',
    role: 'HOA Board President · Decatur, GA',
    text: 'Our apartment community hired FMA Crew for balcony waterproofing and structural restoration on 80+ units. They phased the work so residents were never without their balconies for more than a day or two. The quality is excellent and the project came in on time and on budget.',
    service: 'Multi-Family & Apartment Maintenance · 80+ Units',
    initials: 'RT',
  },
];

const PROCESS = [
  { step: '01', title: 'Free On-Site Assessment', desc: 'We conduct a thorough walk-through of your apartment community and provide a detailed, no-obligation estimate within 24 hours — covering every unit, common area, and exterior element.' },
  { step: '02', title: 'Scoped & Scheduled', desc: 'We present a clear scope of work with unit-by-unit timeline, materials, and milestones. You approve, we schedule around your lease calendar. Simple, no surprises.' },
  { step: '03', title: 'Expert Execution', desc: 'Our licensed crew executes with precision and minimal resident disruption. You get regular progress updates throughout — not silence until a final invoice.' },
  { step: '04', title: 'Final Walkthrough & Sign-Off', desc: 'We walk every unit and space with you before close-out. If anything isn\'t right, we fix it — no charge, no argument. Your residents\' satisfaction is our finish line.' },
];

export default function BrochureFma() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (message.length > 500) return;
    setFormStatus('loading');
    try {
      const form = e.currentTarget;
      const raw = new FormData(form);
      const data = new URLSearchParams();
      raw.forEach((value, key) => data.append(key, value.toString()));
      const res = await fetch('https://readdy.ai/api/form/d70utgj2m2156g4s7440', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setFormStatus(res.ok ? 'success' : 'error');
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <>
      <meta name="robots" content="noindex, nofollow" />
      <div className="min-h-screen bg-white font-sans">
        {/* Header */}
        <header className="bg-[#7C3AED] px-5 md:px-10 py-4 flex items-center justify-between sticky top-0 z-50">
          <div className="flex items-center">
            <div className="h-10 flex items-center">
              <img
                src="https://storage.readdy-site.link/project_files/a1db2c25-97e2-4ceb-a4ec-c06941f029b5/a3ac0cdf-0792-4180-b72e-0df4bd1c7c5e_FMA-CREW-LOGO.png?v=b37410eb2aa41c47106263f8d4f50ddf"
                alt="FMA Crew - Facility Maintenance of America"
                className="h-full w-auto object-contain brightness-0 invert"
              />
            </div>
          </div>
          <a
            href="tel:4702225777"
            className="flex items-center gap-2 bg-white text-[#7C3AED] px-4 py-2 rounded-md text-xs font-bold whitespace-nowrap cursor-pointer hover:bg-purple-50 transition-colors"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-fill" />
            </div>
            470-222-5777
          </a>
        </header>

        {/* Hero */}
        <section className="relative overflow-hidden" style={{ minHeight: '520px' }}>
          <img
            src={HERO_IMG}
            alt="Multi-Family & Apartment Maintenance"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/50" />
          <div className="relative z-10 px-5 md:px-12 py-14 md:py-20 max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#7C3AED]/25 border border-[#A78BFA]/50 px-3 py-1.5 rounded-full mb-5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-community-line text-[#C4B5FD] text-xs" />
              </div>
              <span className="text-[#C4B5FD] text-xs tracking-widest uppercase font-semibold">Multi-Family &amp; Apartment Excellence</span>
            </div>
            <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-4">
              Multi-Family &amp;<br />
              <span className="text-[#C4B5FD]">Apartment Maintenance</span>
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-3 max-w-xl">
              Your Apartment Community, Maintained Better.
            </p>
            <p className="text-white/75 text-sm md:text-base leading-relaxed mb-8 max-w-2xl">
              From unit turns and common area upkeep to structural repairs, balcony restoration, and preventive maintenance programs — FMA Crew is the trusted maintenance partner for apartment communities and multi-family properties across Greater Atlanta.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="tel:4702225777"
                className="flex items-center justify-center gap-2 bg-[#7C3AED] text-white px-6 py-3.5 rounded-md text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-[#6D28D9] transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill" />
                </div>
                Call Now: 470-222-5777
              </a>
              <a
                href="mailto:office@fmacrew.com"
                className="flex items-center justify-center gap-2 bg-white/15 border border-white/35 text-white px-6 py-3.5 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-white/25 transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line" />
                </div>
                office@fmacrew.com
              </a>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#EDE9FE] px-5 md:px-10 py-5">
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: 'ri-award-line', value: 'GA Licensed', label: 'State Licensed & Insured' },
              { icon: 'ri-community-line', value: 'Multi-Family', label: 'Apartment Specialist' },
              { icon: 'ri-alarm-warning-line', value: '2–4 Hrs', label: 'Emergency Response Time' },
              { icon: 'ri-thumb-up-line', value: 'A+ Standard', label: 'Workmanship Quality' },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-[#7C3AED]/15 text-[#7C3AED] rounded shrink-0">
                  <i className={`${stat.icon} text-base`} />
                </div>
                <div>
                  <p className="text-[#5B21B6] font-bold text-sm md:text-base leading-tight">{stat.value}</p>
                  <p className="text-[#7C3AED]/65 text-xs leading-tight">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* About Section */}
        <section className="bg-white px-5 md:px-10 py-12 md:py-16">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <span className="text-[#7C3AED] text-xs tracking-widest uppercase font-semibold">About FMA Crew</span>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-3 mb-4 leading-tight">
                  Your Trusted Partner in<br />
                  <span className="text-[#5B21B6]">Multi-Family &amp; Apartment Maintenance</span>
                </h2>
                <div className="w-10 h-0.5 bg-[#7C3AED] mb-5" />
                <p className="text-slate-600 text-sm md:text-base leading-relaxed mb-4">
                  <strong>FMA Crew — Facility Maintenance of America</strong> — is a full-service building maintenance and construction company specializing in multi-family housing and apartment communities across the Greater Atlanta area.
                </p>
                <p className="text-slate-600 text-sm md:text-base leading-relaxed mb-4">
                  From apartment unit turns and make-readys to common area upkeep, structural repairs, and full-scale preventive maintenance programs — our experienced team delivers consistent, high-quality results that protect your asset, satisfy your residents, and keep your property operating at its best.
                </p>
                <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                  Whether you manage a single apartment community or a large portfolio of multi-family properties, FMA Crew is the one team that handles it all — from emergency repairs to long-term facility contracts.
                </p>
              </div>
              <div className="relative">
                <div className="overflow-hidden h-[280px] md:h-[380px] w-full rounded-lg">
                  <img
                    src="https://public.readdy.ai/ai/img_res/7df9b6c0ada455fab0ad72dd8bf2f577.jpg"
                    alt="FMA Crew apartment maintenance quality"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-4 -right-2 md:-bottom-5 md:-right-4 text-white p-4 w-40 rounded-xl" style={{ background: 'linear-gradient(135deg, #6D28D9 0%, #7C3AED 55%, #A78BFA 100%)' }}>
                  <div className="w-7 h-7 flex items-center justify-center bg-white/20 mb-2">
                    <i className="ri-medal-line text-base" />
                  </div>
                  <p className="text-sm font-bold leading-tight">Excellence First</p>
                  <p className="text-white/80 text-xs mt-1 leading-snug">Our Standard on Every Property</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="bg-[#FAF5FF] px-5 md:px-10 py-12 md:py-16">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8 md:mb-10">
              <span className="text-[#7C3AED] text-xs tracking-widest uppercase font-semibold">Our Services</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-3">
                Multi-Family &amp; Apartment Maintenance —{' '}
                <span className="text-[#5B21B6]">Complete Coverage</span>
              </h2>
              <p className="text-slate-500 text-sm md:text-base mt-2 max-w-2xl leading-relaxed">
                From unit turns and emergency structural repairs to long-term preventive maintenance contracts — one team, one standard, all covered for your apartment community.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {SERVICES.map((svc) => (
                <div key={svc.title} className="bg-white border border-purple-100 p-5 hover:border-[#7C3AED] transition-all duration-200 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#EDE9FE] text-[#7C3AED] rounded mb-3">
                    <i className={`${svc.icon} text-lg`} />
                  </div>
                  <h3 className="text-slate-900 font-bold text-sm mb-2">{svc.title}</h3>
                  <p className="text-slate-500 text-xs leading-relaxed">{svc.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 p-5 bg-[#7C3AED] rounded-lg">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-white font-bold text-base">Need a custom maintenance program for your apartment community?</p>
                  <p className="text-purple-200 text-sm mt-1">We design tailored preventive maintenance contracts for apartment communities of any size — monthly, quarterly, or semi-annual.</p>
                </div>
                <a
                  href="tel:4702225777"
                  className="flex items-center gap-2 bg-white text-[#7C3AED] px-5 py-3 rounded-md text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-purple-50 transition-colors shrink-0"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-fill" />
                  </div>
                  Get a Free Quote
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Service Area Map */}
        <section className="bg-[#FAF5FF] px-5 md:px-10 py-12 md:py-16">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <span className="text-[#7C3AED] text-xs tracking-widest uppercase font-semibold">Service Area</span>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-3 mb-4 leading-tight">
                  Serving Greater Atlanta<br />
                  <span className="text-[#5B21B6]">Apartment Communities</span>
                </h2>
                <div className="w-10 h-0.5 bg-[#7C3AED] mb-5" />
                <p className="text-slate-600 text-sm leading-relaxed mb-5">
                  Our licensed crews are positioned across the Greater Atlanta metro — ready to respond within hours, not days.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {['Norcross', 'Marietta', 'Alpharetta', 'Decatur', 'Roswell', 'Sandy Springs', 'Duluth', 'Lawrenceville'].map((city) => (
                    <div key={city} className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-2-fill text-[#7C3AED] text-xs" />
                      </div>
                      <span className="text-slate-700 text-sm">{city}</span>
                    </div>
                  ))}
                </div>
                <p className="text-slate-500 text-xs mt-4">+ surrounding 50-mile radius across the Atlanta metro area</p>
              </div>
              <div className="rounded-xl overflow-hidden border border-purple-100 h-[320px] md:h-[380px]">
                <iframe
                  title="FMA Crew Service Area - Greater Atlanta"
                  src="https://maps.google.com/maps?q=Atlanta,Georgia,USA&z=9&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose FMA */}
        <section className="bg-[#F5F3FF] px-5 md:px-10 py-12 md:py-16">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <span className="text-[#7C3AED] text-xs tracking-widest uppercase font-semibold">Why Choose Us</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-3">
                The FMA Crew <span className="text-[#7C3AED]">Difference</span>
              </h2>
              <p className="text-slate-500 text-sm md:text-base mt-3 max-w-xl mx-auto leading-relaxed">
                We combine professional expertise, transparent communication, and genuine care for every apartment community and multi-family property we serve.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {WHY_ITEMS.map((item) => (
                <div key={item.title} className="bg-white border border-purple-100 p-5 rounded-xl hover:border-[#7C3AED]/50 transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#EDE9FE] text-[#7C3AED] rounded mb-3">
                    <i className={`${item.icon} text-lg`} />
                  </div>
                  <h3 className="text-slate-900 font-bold text-sm mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="bg-white px-5 md:px-10 py-12 md:py-16">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8 md:mb-10">
              <span className="text-[#7C3AED] text-xs tracking-widest uppercase font-semibold">How It Works</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-3">
                Simple Process. <span className="text-[#5B21B6]">Zero Surprises.</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {PROCESS.map((p) => (
                <div key={p.step} className="flex gap-4 p-5 bg-[#FAF5FF] border border-purple-100 rounded-lg">
                  <div className="shrink-0">
                    <span className="text-2xl font-black text-[#7C3AED] leading-none">{p.step}</span>
                  </div>
                  <div>
                    <h3 className="text-slate-900 font-bold text-sm mb-1.5">{p.title}</h3>
                    <p className="text-slate-500 text-xs leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="bg-[#FAF5FF] px-5 md:px-10 py-12 md:py-16">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8">
              <span className="text-[#7C3AED] text-xs tracking-widest uppercase font-semibold">Client Reviews</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-3">
                What Apartment Managers Say
              </h2>
            </div>

            <div className="bg-white border border-purple-100 rounded-lg p-6 md:p-8 mb-4">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map((s) => (
                  <div key={s} className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-amber-400 text-sm" />
                  </div>
                ))}
              </div>
              <p className="text-slate-700 text-sm md:text-base leading-relaxed mb-5 italic">
                &ldquo;{TESTIMONIALS[activeTestimonial].text}&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#7C3AED] text-white rounded-full text-xs font-bold shrink-0">
                  {TESTIMONIALS[activeTestimonial].initials}
                </div>
                <div>
                  <p className="text-slate-900 font-bold text-sm">{TESTIMONIALS[activeTestimonial].name}</p>
                  <p className="text-slate-500 text-xs">{TESTIMONIALS[activeTestimonial].role}</p>
                </div>
                <div className="ml-auto">
                  <span className="px-3 py-1 bg-[#EDE9FE] text-[#7C3AED] text-xs font-semibold rounded-full">
                    {TESTIMONIALS[activeTestimonial].service}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mb-6">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTestimonial(i)}
                  className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${activeTestimonial === i ? 'w-6 bg-[#7C3AED]' : 'w-2 bg-slate-300'}`}
                />
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: 'ri-shield-check-line', label: 'Licensed & Insured', sub: 'Georgia State Licensed' },
                { icon: 'ri-community-line', label: 'Apartment Specialist', sub: 'Multi-Family Communities' },
                { icon: 'ri-star-line', label: '5-Star Rated', sub: 'Pursuit of Excellence' },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-3 p-4 bg-white border border-purple-100 rounded-lg">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#EDE9FE] text-[#7C3AED] rounded shrink-0">
                    <i className={`${b.icon} text-base`} />
                  </div>
                  <div>
                    <p className="text-slate-900 font-bold text-xs">{b.label}</p>
                    <p className="text-slate-500 text-xs">{b.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Inquiry Form */}
        <section className="bg-white px-5 md:px-10 py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <span className="text-[#7C3AED] text-xs tracking-widest uppercase font-semibold">Free Estimate</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-3">Request a Free Quote</h2>
              <p className="text-slate-500 text-sm mt-2 max-w-lg mx-auto">Fill out the form and we&apos;ll get back to you within 24 hours — no obligation, no cost.</p>
            </div>

            {formStatus === 'success' ? (
              <div className="bg-[#EDE9FE] border border-purple-200 rounded-lg p-10 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-[#7C3AED] text-white rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-2xl" />
                </div>
                <h3 className="text-slate-900 font-bold text-lg mb-2">Message Received!</h3>
                <p className="text-slate-600 text-sm">Our team will reach out to you within 24 hours to schedule your free on-site assessment.</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="fma-inquiry-form"
                onSubmit={handleFormSubmit}
                className="bg-[#FAF5FF] border border-purple-100 rounded-lg p-6 md:p-8"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-slate-700 text-xs font-semibold mb-1.5">Full Name <span className="text-[#7C3AED]">*</span></label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="John Smith"
                      className="w-full border border-purple-100 bg-white rounded-md px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#7C3AED] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 text-xs font-semibold mb-1.5">Email Address <span className="text-[#7C3AED]">*</span></label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      className="w-full border border-purple-100 bg-white rounded-md px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#7C3AED] transition-colors"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-slate-700 text-xs font-semibold mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="(000) 000-0000"
                    className="w-full border border-purple-100 bg-white rounded-md px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#7C3AED] transition-colors"
                  />
                </div>
                <div className="mb-5">
                  <label className="block text-slate-700 text-xs font-semibold mb-1.5">Message <span className="text-[#7C3AED]">*</span></label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="Briefly describe your property and the maintenance or repair needs..."
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    className="w-full border border-purple-100 bg-white rounded-md px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#7C3AED] transition-colors resize-none"
                  />
                  <p className="text-xs text-slate-400 text-right mt-1">{message.length}/500</p>
                </div>
                {formStatus === 'error' && (
                  <p className="text-red-500 text-xs mb-3">Something went wrong. Please try again or call us directly.</p>
                )}
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full bg-[#7C3AED] text-white py-3 rounded-md text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-[#6D28D9] transition-colors disabled:opacity-60"
                >
                  {formStatus === 'loading' ? 'Sending...' : 'Send Free Estimate Request'}
                </button>
              </form>
            )}
          </div>
        </section>

        {/* CTA Footer */}
        <section className="bg-[#7C3AED] px-5 md:px-10 py-12 md:py-16">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-2xl md:text-4xl font-bold text-white leading-tight">
                Ready to Protect Your Apartment Community?
              </h2>
              <p className="text-purple-200 text-sm md:text-base mt-3 max-w-xl mx-auto leading-relaxed">
                Get a detailed, no-obligation estimate within 24 hours. We respond fast — because your residents and your asset can&apos;t wait.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {[
                { icon: 'ri-phone-line', label: 'Phone', value: '470-222-5777', href: 'tel:4702225777' },
                { icon: 'ri-mail-line', label: 'Email', value: 'office@fmacrew.com', href: 'mailto:office@fmacrew.com' },
                { icon: 'ri-time-line', label: 'Hours', value: 'Mon–Fri: 9AM–5PM', href: undefined },
              ].map((info) => (
                <div key={info.label} className="flex items-center gap-3 p-4 bg-white/15 border border-white/25 rounded-lg">
                  <div className="w-9 h-9 flex items-center justify-center bg-white/20 text-white rounded shrink-0">
                    <i className={`${info.icon} text-base`} />
                  </div>
                  <div>
                    <p className="text-purple-200 text-xs">{info.label}</p>
                    {info.href ? (
                      <a href={info.href} className="text-white font-semibold text-sm hover:text-purple-200 transition-colors cursor-pointer">{info.value}</a>
                    ) : (
                      <p className="text-white font-semibold text-sm">{info.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:4702225777"
                className="flex items-center justify-center gap-2 bg-white text-[#7C3AED] px-8 py-4 rounded-md text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-purple-50 transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill" />
                </div>
                Call Now for Free Estimate
              </a>
              <a
                href="mailto:office@fmacrew.com"
                className="flex items-center justify-center gap-2 bg-white/15 border border-white/30 text-white px-8 py-4 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-white/25 transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-send-line" />
                </div>
                Email Us Directly
              </a>
            </div>
          </div>
        </section>

        {/* Footer Strip */}
        <div className="bg-[#F5F3FF] border-t border-purple-100 px-5 py-4 text-center">
          <p className="text-[#7C3AED]/60 text-xs">© 2026 FMA Crew — Facility Maintenance of America. All rights reserved. · GA Licensed &amp; Insured</p>
        </div>
      </div>
    </>
  );
}
